/**
 * useContractDisclosure.ts — the one figure the pre-contract disclosure needs and the
 * insights payload does not carry.
 *
 * The hire purchase Schedule's money grid states two prices: the Cash price, at which the
 * Vehicle could be bought outright, and the Hire-purchase price, the total payable to
 * complete the purchase. The Schedule then has the Driver state that BOTH "were stated to
 * the Driver, orally and in writing and otherwise than in this Agreement, before this
 * Agreement was made" — the disclosure section 2 of the Hire-Purchase Act, 1974 (NRCD 292)
 * requires. The ceremony's `disclose` step is where the app makes that true of itself.
 *
 * `get_contract_insights` resolves the Hire-purchase price as `contract.sold_at` and does not
 * project a cash price at all (verified against the function body on staging
 * azmqqvhyndtqhjfdpfjz — `cash_price` appears nowhere in it). The column exists on
 * `contracts`, and `contracts_select` admits `driver_id = my_driver_id()`, so the signer can
 * read their own row directly. That is what this hook is.
 *
 * ── IT IS ITS OWN QUERY, AND THAT IS THE DESIGN ───────────────────────────────
 * `contracts.cash_price` was added by fleet-backend `20260816010000` and exists on STAGING
 * ONLY until that promotes. PostgREST answers a select naming a column that is not there with
 * a 42703 and fails the WHOLE request — so folding this column into `useDriverContract`'s
 * select, or into any other read the contract page depends on, would take the page down on
 * production the moment this app shipped ahead of the migration. Held apart, the identical
 * failure costs one panel on one step of one ceremony.
 *
 * ── AND IT NEVER THROWS ───────────────────────────────────────────────────────
 * Every failure resolves to `cashPrice: null`, which the panel renders as the form that
 * states no figure. A disclosure screen that showed an error would be a disclosure screen
 * that disclosed nothing, and the pre-promote window is an expected state rather than a
 * fault, so the known-absence codes are not reported. Anything else is reported and still
 * degrades.
 *
 * NOTHING IS EVER DERIVED FROM `sold_at`. A cash price back-filled from the hire-purchase
 * price would print the two figures equal, and a screen stating they are equal states that
 * this contract carries no charge for credit — on the exact screen whose job is to disclose
 * that charge.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';

export interface ContractDisclosure {
    /** GHS, or null when the column, the row or the read is unavailable. */
    cashPrice: number | null;
}

const ABSENT: ContractDisclosure = { cashPrice: null };

/**
 * The shapes that mean "not on this database yet" rather than "something is wrong".
 *
 * `42703` is Postgres's undefined_column, which is precisely the pre-promote window this
 * hook is built around; `42P01` its undefined_table. PGRST202 / PGRST205 are PostgREST's own
 * pair, kept for symmetry with `useAgreements.ts:161-164`.
 */
const ABSENCE_CODES = new Set(['42703', '42P01', 'PGRST202', 'PGRST204', 'PGRST205']);

function isColumnAbsent(error: unknown): boolean {
    const code = (error as { code?: string } | null)?.code;
    return typeof code === 'string' && ABSENCE_CODES.has(code);
}

export function useContractDisclosure(
    contractId: number | null | undefined,
    options: { enabled?: boolean } = {},
) {
    return useQuery({
        queryKey: ['contract-disclosure', contractId],
        queryFn: async (): Promise<ContractDisclosure> => {
            const { data, error } = await (supabase as any)
                .from('contracts')
                .select('id, cash_price')
                .eq('id', contractId)
                .maybeSingle();

            if (error) {
                if (!isColumnAbsent(error)) {
                    captureError(error, { tags: { area: 'agreement-disclosure' } });
                }
                return ABSENT;
            }

            const raw = (data as { cash_price?: number | string | null } | null)?.cash_price;
            const value = typeof raw === 'string' ? Number(raw) : raw;
            return {
                cashPrice:
                    typeof value === 'number' && Number.isFinite(value) && value > 0
                        ? value
                        : null,
            };
        },
        // The disclosure is only ever read on a hire purchase ceremony; every other document
        // type passes `enabled: false` rather than spending a round trip on a figure its own
        // screen never shows.
        enabled: !!contractId && options.enabled !== false,
        staleTime: 5 * 60_000,
        retry: false,
    });
}
